import { Auth0ProviderWithNavigate } from "./auth0/auth0-provider-with-navigate";
import React, { useEffect } from "react";
import { createSearchParams, useNavigate, useParams } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import { ReAuthProvider } from "context/ReAuthContext";

import i18n from "translations/config";

import { App } from "App";

import { useRootDispatch } from "store";
import { useRequestDispatch } from "store/requestHooks";
import {
  createDefaultConfig,
  FetchConfig,
  fetchConfig,
  updateConfig,
} from "store/config";
import { useRootSelector } from "store/hooks";
import { ERROR_TYPES } from "constants/common";
import { GenericActionError } from "store/common";

export interface ClientRootProps {}

export const ClientRoot: React.FC<ClientRootProps> = () => {
  const navigate = useNavigate();
  const { customerKey } = useParams();
  const dispatch = useRootDispatch();
  const requestDispatch = useRequestDispatch();
  const { error: clientConfigFetchError, client } = useRootSelector(
    (state) => state.config,
  );

  useEffect(() => {
    if (!!customerKey) {
      const idPId = localStorage.getItem(customerKey);

      if (idPId) {
        dispatch(updateConfig(createDefaultConfig(customerKey, idPId)));
      }

      requestDispatch<FetchConfig, { key: string }>(fetchConfig, {
        key: customerKey,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (clientConfigFetchError) {
      const theError = clientConfigFetchError as GenericActionError;
      if (!!(window as any).dtrum) {
        const enterActionId = "client-config-fetch-error";
        (window as any).dtrum.reportError(theError, enterActionId);
      }
      switch (theError.status) {
        case 404:
          navigate({
            pathname: "/",
            search: `?${createSearchParams({
              error: ERROR_TYPES.CLIENT_NOT_FOUND,
              error_description: `The client site for "${customerKey?.toLowerCase()}" could not be located.`,
            })}`,
          });
          break;
        default:
          navigate({
            pathname: "/",
            search: `?${createSearchParams({
              error: ERROR_TYPES.CONFIGURATION_ERROR,
              error_description:
                "The site's configuration failed to load. Please try again later or contact your administrator.",
            })}`,
          });
          break;
      }
    } else if (customerKey === client.idPId) {
      navigate({
        pathname: `/${client.customerKey}`,
        search: window.location.search,
      });
    }
  }, [
    customerKey,
    client.idPId,
    client.customerKey,
    clientConfigFetchError,
    navigate,
  ]);

  return (
    <ReAuthProvider>
      <Auth0ProviderWithNavigate>
        <I18nextProvider i18n={i18n}>
          <App />
        </I18nextProvider>
      </Auth0ProviderWithNavigate>
    </ReAuthProvider>
  );
};
